import React, { useMemo, useRef, useState } from 'react';

const CLEAR_TIMEOUT_MS = 10000;

type Value = {
  announcePolite: (announcement: string) => void;
  announceAssertive: (announcement: string) => void;
};

const AriaLiveContext = React.createContext<Value>({
  announceAssertive: () => {
    console.log('announceAssertive function not implemented');
  },
  announcePolite: () => {
    console.log('announcePolite function not implemented');
  },
});

export const AriaLiveProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [polites, setPoliteAnnouncement] = useState<string[]>([]);
  const [assertives, announceAssertive] = useState<string>();
  const clearTimerRef = useRef<number>(null);

  const announcePolite = useMemo(
    () => (announcement: string) => {
      if (clearTimerRef.current) {
        window.clearTimeout(clearTimerRef.current);
      }
      setPoliteAnnouncement(prevState => {
        return [...prevState, announcement];
      });
      clearTimerRef.current = window.setTimeout(() => {
        setPoliteAnnouncement([]);
      }, CLEAR_TIMEOUT_MS);
    },
    []
  );

  const value = useMemo<Value>(
    () => ({ announcePolite, announceAssertive }),
    [announcePolite, announceAssertive]
  );

  return (
    <AriaLiveContext.Provider value={value}>
      <div role="status" aria-live="assertive" aria-atomic="true">
        {assertives}
      </div>
      <div role="status" aria-live="polite" aria-atomic="false">
        {polites.map((a, i) => (
          <p key={`arialive${i}`}>{a}</p>
        ))}
      </div>
      {children}
    </AriaLiveContext.Provider>
  );
};

export function useAriaLive() {
  return React.useContext(AriaLiveContext);
}
